// Encode a workout template into a portable, URL-safe share code.
// Exercises are referenced by name so codes work across installs.

export function encodeWorkout(workout) {
  const payload = {
    v: 1,
    name: workout.name,
    exercises: (workout.exercises || []).map((e) => ({
      exercise_name: e.exercise_name,
      target_sets: e.target_sets,
      target_reps_min: e.target_reps_min,
      target_reps_max: e.target_reps_max,
      rest_seconds: e.rest_seconds,
      target_weight_kg: e.target_weight_kg,
      // Cardio/mobility targets too, so sharing a duration/distance template keeps its
      // prescription (the import handler clamps these the same way it does reps/weight).
      target_duration_seconds: e.target_duration_seconds,
      target_distance_m: e.target_distance_m,
    })),
  };
  const json = JSON.stringify(payload);
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeWorkout(code) {
  try {
    let b64 = String(code).trim().replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function shareUrl(code) {
  return `${location.origin}/import?code=${code}`;
}

/**
 * Hand text to the OS share sheet, falling back to the clipboard where there isn't one (desktop
 * browsers, and the Android WebView when the page isn't in a user-gesture context).
 * Resolves to "shared" | "copied" | "unavailable" so the caller can say what actually happened
 * rather than claiming success either way. A cancelled share sheet is not an error — it's a
 * user changing their mind — so it resolves rather than rejects.
 */
export async function shareText(text, { title } = {}) {
  if (navigator.share) {
    try {
      await navigator.share({ title, text });
      return "shared";
    } catch {
      // AbortError (dismissed) and NotAllowedError (no gesture) land here alike; fall through
      // to the clipboard so the content is at least in the user's hands.
    }
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return "copied";
    } catch { /* denied or insecure context */ }
  }
  return "unavailable";
}
